/**
 * Every call the auth feature makes. Nothing here knows about React.
 *
 * Backend surface — /api/superadmin/auth
 *   POST   /login            public,    returns { token, expiresIn, user }
 *   POST   /refresh          public,    returns { token, expiresIn, user }
 *   POST   /forgot-password  public,    returns { message }
 *   POST   /reset-password   public,    returns { message }
 *   GET    /me               protected, returns { user }
 *   POST   /change-password  protected, returns { message }
 *   POST   /logout           protected, returns { message }
 *
 * SA-7 note: the refresh token is NOT handled here and must not be. It lives in
 * an httpOnly cookie the browser attaches by itself, and `packages/api-client`
 * performs the silent refresh on a 401. Nothing in this file — or in any
 * component — can read it, which is the point.
 */
import { apiClient } from '@crestrewards/api-client';

/**
 * POST /api/superadmin/auth/login
 *
 * `skipAuthRedirect` keeps a wrong password on this page as an inline error
 * instead of triggering the global 401 bounce to /login.
 *
 * @param {{ email: string, password: string }} credentials
 * @returns {Promise<{ token: string, expiresIn: string, user: object }>}
 */
export function login({ email, password }) {
  return apiClient.post(
    '/superadmin/auth/login',
    { email, password },
    { skipAuthRedirect: true },
  );
}

/**
 * GET /api/superadmin/auth/me — used to restore a session on reload.
 *
 * `skipAuthRedirect` defaults to true so useAuth can settle its own state
 * machine rather than being interrupted by a hard redirect mid-restore. Pass a
 * `timeout` to bound how long the caller is willing to wait.
 *
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<{ user: object }>}
 */
export function getCurrentUser(config = {}) {
  return apiClient.get('/superadmin/auth/me', {
    skipAuthRedirect: true,
    ...config,
  });
}

/**
 * POST /api/superadmin/auth/logout
 *
 * No longer a courtesy call. As of SA-7 it revokes the refresh token
 * server-side and clears the cookie, so signing out genuinely ends the session
 * rather than only forgetting it on this device.
 */
export function logout() {
  return apiClient.post('/superadmin/auth/logout', null, { skipAuthRedirect: true });
}

/**
 * POST /api/superadmin/auth/forgot-password
 *
 * **Always resolves**, whether or not the email belongs to an account — the
 * backend deliberately returns an identical response either way so the endpoint
 * cannot be used to discover which addresses have accounts. The UI must show
 * the same confirmation in both cases; do not add a "no such user" branch.
 *
 * @param {{ email: string }} input
 * @returns {Promise<{ message: string }>}
 */
export function forgotPassword({ email }) {
  return apiClient.post(
    '/superadmin/auth/forgot-password',
    { email },
    { skipAuthRedirect: true },
  );
}

/**
 * POST /api/superadmin/auth/reset-password
 *
 * The token comes from the emailed link's query string. Rejects with
 * `RESET_TOKEN_INVALID` for an unknown, expired or already-used link.
 *
 * @param {{ token: string, password: string }} input
 * @returns {Promise<{ message: string }>}
 */
export function resetPassword({ token, password }) {
  return apiClient.post(
    '/superadmin/auth/reset-password',
    { token, password },
    { skipAuthRedirect: true },
  );
}

/**
 * POST /api/superadmin/auth/change-password — for a signed-in user.
 *
 * Requires the current password even though the caller is authenticated, so an
 * unattended signed-in browser is not enough to take the account over. Every
 * OTHER session is revoked; this one survives.
 *
 * @param {{ currentPassword: string, newPassword: string }} input
 * @returns {Promise<{ message: string }>}
 */
export function changePassword({ currentPassword, newPassword }) {
  return apiClient.post('/superadmin/auth/change-password', {
    currentPassword,
    newPassword,
  });
}
